import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck, Check, ChevronDown } from "lucide-react";
import { Logo } from "@/components/site/ui";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiError, getUTM, inr, track } from "@/lib/api";
import { getCart } from "@/lib/cart";
import { toast } from "sonner";

const CATEGORIES = ["Retail", "Restaurant", "Salon & Beauty", "Healthcare", "Education", "Real Estate", "Fashion", "Manufacturing", "Professional Services", "D2C", "Local Business", "Startup", "Other"];

function loadRazorpay() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

export default function Checkout() {
    const nav = useNavigate();
    const cart = useMemo(() => getCart(), []);
    const [step, setStep] = useState(1);
    const [quote, setQuote] = useState(null);
    const [form, setForm] = useState({});
    const [loading, setLoading] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    useEffect(() => {
        track("checkout_started", cart);
        if (!cart.package_slug && (!cart.service_slugs || cart.service_slugs.length === 0)) { nav("/services"); return; }
        api.post("/checkout/quote", { service_slugs: cart.service_slugs, package_slug: cart.package_slug })
            .then(({ data }) => setQuote(data)).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); nav("/services"); });
    }, [cart, nav]);

    const detailsValid = form.customer_name && form.mobile && form.email;

    const pay = async () => {
        if (!detailsValid) { toast.error("Please fill required business details"); setStep(2); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/orders", {
                customer_name: form.customer_name, business_name: form.business_name || "", mobile: form.mobile,
                whatsapp: form.whatsapp || "", email: form.email, city: form.city || "", state: form.state || "",
                business_category: form.business_category || "", website: form.website || "", instagram: form.instagram || "",
                gmb_url: form.gmb_url || "", service_slugs: cart.service_slugs, package_slug: cart.package_slug,
                additional_requirements: form.additional_requirements || "", utm: getUTM(),
            });
            track("payment_started", { order_id: data.order_id, amount: data.amount });

            if (!data.payment_configured) {
                toast.success("Your request has been received. Our BDE will share a secure payment link shortly.");
                nav(`/thank-you/${data.order_id}`);
                return;
            }
            const ok = await loadRazorpay();
            if (!ok) { toast.error("Could not load payment gateway"); setLoading(false); return; }
            const rzp = new window.Razorpay({
                key: data.razorpay_key_id, amount: Math.round(data.amount * 100), currency: "INR",
                name: "SUDIGI", description: "Digital Growth Services", order_id: data.razorpay_order_id,
                prefill: { name: form.customer_name, email: form.email, contact: form.mobile },
                theme: { color: "#072E29" },
                handler: async (resp) => {
                    try {
                        await api.post("/payments/verify", {
                            order_id: data.order_id, razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature,
                        });
                        track("payment_successful", { order_id: data.order_id });
                        nav(`/thank-you/${data.order_id}`);
                    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Verification failed"); }
                },
                modal: { ondismiss: () => setLoading(false) },
            });
            rzp.open();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
            setLoading(false);
        }
    };

    const STEPS = ["Your Service", "Business Details", "Payment"];

    return (
        <div className="min-h-screen bg-teal-900 text-cream">
            <header className="border-b border-brandborder/60 py-4"><div className="container-x flex items-center justify-between"><Logo /><button onClick={() => nav(-1)} className="text-sm text-cream-soft hover:text-gold flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button></div></header>

            {/* mobile summary */}
            <div className="lg:hidden border-b border-brandborder/60">
                <button onClick={() => setSummaryOpen((o) => !o)} className="container-x w-full flex justify-between items-center py-3" data-testid="mobile-summary-toggle">
                    <span className="text-sm text-cream-soft">Order Total</span>
                    <span className="flex items-center gap-2 font-mono font-bold text-gold">{inr(quote?.total || 0)} <ChevronDown className={`w-4 h-4 transition-transform ${summaryOpen ? "rotate-180" : ""}`} /></span>
                </button>
                {summaryOpen && <div className="container-x pb-4"><Summary quote={quote} /></div>}
            </div>

            <div className="container-x py-8 grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                    <div className="flex items-center gap-2 mb-8">
                        {STEPS.map((s, i) => (
                            <div key={s} className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step > i + 1 ? "bg-growth text-teal-900" : step === i + 1 ? "bg-gold text-teal-900" : "bg-teal-700 text-cream-soft"}`}>
                                    {step > i + 1 ? <Check className="w-4 h-4" /> : i + 1}
                                </div>
                                <span className={`text-sm hidden sm:block ${step === i + 1 ? "text-cream font-semibold" : "text-cream-soft"}`}>{s}</span>
                                {i < STEPS.length - 1 && <div className="w-6 sm:w-10 h-px bg-brandborder" />}
                            </div>
                        ))}
                    </div>

                    {step === 1 && (
                        <div className="card-teal rounded-2xl p-6" data-testid="checkout-step-1">
                            <h2 className="font-serif text-2xl text-cream mb-4">Your Selection</h2>
                            <div className="space-y-3">
                                {(quote?.items || []).map((it) => (
                                    <div key={it.slug} className="flex justify-between items-center p-4 rounded-xl bg-teal-800/50 border border-brandborder">
                                        <div><div className="font-semibold text-cream">{it.name}</div><div className="text-xs text-cream-soft/70 uppercase">{it.type}</div></div>
                                        <div className="font-mono font-bold text-cream">{inr(it.price)}</div>
                                    </div>
                                ))}
                            </div>
                            <button onClick={() => setStep(2)} className="btn-gold w-full mt-6 rounded-full py-3 flex items-center justify-center gap-2" data-testid="checkout-step-1-next">Continue <ArrowRight className="w-4 h-4" /></button>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="card-teal rounded-2xl p-6" data-testid="checkout-step-2">
                            <h2 className="font-serif text-2xl text-cream mb-4">Business Details</h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <F label="Customer Name *"><CInput v={form.customer_name} on={set("customer_name")} t="checkout-name" /></F>
                                <F label="Business Name"><CInput v={form.business_name} on={set("business_name")} t="checkout-business" /></F>
                                <F label="Mobile Number *"><CInput v={form.mobile} on={set("mobile")} t="checkout-mobile" /></F>
                                <F label="WhatsApp Number"><CInput v={form.whatsapp} on={set("whatsapp")} t="checkout-whatsapp" /></F>
                                <F label="Email *"><CInput v={form.email} on={set("email")} t="checkout-email" /></F>
                                <F label="City"><CInput v={form.city} on={set("city")} t="checkout-city" /></F>
                                <F label="State"><CInput v={form.state} on={set("state")} t="checkout-state" /></F>
                                <F label="Business Category">
                                    <select className="w-full h-9 rounded-md bg-teal-800/50 border border-brandborder px-3 text-sm text-cream" value={form.business_category || ""} onChange={set("business_category")} data-testid="checkout-category">
                                        <option value="">Select</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </F>
                                <F label="Website URL"><CInput v={form.website} on={set("website")} t="checkout-website" /></F>
                                <F label="Instagram URL"><CInput v={form.instagram} on={set("instagram")} t="checkout-instagram" /></F>
                                <F label="Google Business Profile URL"><CInput v={form.gmb_url} on={set("gmb_url")} t="checkout-gmb" /></F>
                            </div>
                            <div className="mt-4"><F label="Additional Requirements"><Textarea rows={3} className="bg-teal-800/50 border-brandborder text-cream" value={form.additional_requirements || ""} onChange={set("additional_requirements")} data-testid="checkout-requirements" /></F></div>
                            <div className="flex gap-3 mt-6">
                                <button onClick={() => setStep(1)} className="btn-ghost-cream rounded-full px-6 py-3">Back</button>
                                <button onClick={() => { if (!detailsValid) { toast.error("Name, mobile and email are required"); return; } setStep(3); }} className="btn-gold flex-1 rounded-full py-3 flex items-center justify-center gap-2" data-testid="checkout-step-2-next">Continue to Payment <ArrowRight className="w-4 h-4" /></button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="card-teal rounded-2xl p-6" data-testid="checkout-step-3">
                            <h2 className="font-serif text-2xl text-cream mb-4">Payment</h2>
                            <div className="rounded-xl bg-teal-800/50 border border-brandborder p-4 flex items-center gap-3 mb-4">
                                <ShieldCheck className="w-6 h-6 text-growth" />
                                <div className="text-sm text-cream-soft">Secure payment via Razorpay — UPI, Cards & Net Banking supported.</div>
                            </div>
                            <div className="flex flex-wrap gap-2 mb-5">{["UPI", "Card", "Net Banking"].map((m) => <span key={m} className="text-xs border border-brandborder rounded-full px-3 py-1.5 text-cream-soft">{m}</span>)}</div>
                            <div className="flex gap-3">
                                <button onClick={() => setStep(2)} className="btn-ghost-cream rounded-full px-6 py-3">Back</button>
                                <button onClick={pay} disabled={loading} className="btn-gold flex-1 rounded-full py-3 flex items-center justify-center gap-2" data-testid="checkout-pay-btn">
                                    {loading && <Loader2 className="w-4 h-4 animate-spin" />} Pay {inr(quote?.total || 0)}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <aside className="hidden lg:block">
                    <div className="card-teal rounded-2xl p-6 sticky top-6"><h3 className="font-serif text-xl text-cream mb-4">Order Summary</h3><Summary quote={quote} /></div>
                </aside>
            </div>
        </div>
    );
}

function Summary({ quote }) {
    if (!quote) return <div className="text-cream-soft text-sm">Loading…</div>;
    return (
        <div>
            <ul className="space-y-2 mb-4">{quote.items.map((it) => <li key={it.slug} className="flex justify-between text-sm text-cream-soft"><span>{it.name}</span><span className="font-mono">{inr(it.price)}</span></li>)}</ul>
            <div className="border-t border-brandborder pt-3 space-y-2 text-sm">
                <div className="flex justify-between text-cream-soft"><span>Subtotal</span><span className="font-mono">{inr(quote.subtotal)}</span></div>
                <div className="flex justify-between text-cream-soft"><span>GST ({quote.gst_rate}%)</span><span className="font-mono">{inr(quote.gst)}</span></div>
                <div className="flex justify-between pt-2 border-t border-brandborder"><span className="font-bold text-cream">Total payable</span><span className="font-mono font-bold text-xl text-gold" data-testid="checkout-total">{inr(quote.total)}</span></div>
            </div>
        </div>
    );
}

function F({ label, children }) { return <div className="space-y-1.5"><Label className="text-cream-soft text-sm">{label}</Label>{children}</div>; }
function CInput({ v, on, t }) { return <Input className="bg-teal-800/50 border-brandborder text-cream" value={v || ""} onChange={on} data-testid={t} />; }
